/* @img-migrated */
import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import NotificationBell from '../NotificationBell';
import PageTransition from '../PageTransition';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  Home,
  User,
  LayoutDashboard,
  Calendar,
  Users,
  Send,
  FileText,
  Share2,
  Building2,
  MessageCircle,
  Headphones,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Globe,
  Mail,
  Settings,
  Award,
  Gift
} from 'lucide-react';

import Img from '../Img';
import { resolveImg } from '../../lib/images';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

import ForcePasswordChange from '../ForcePasswordChange';

const getPortalLinks = (userId, user) => {
  const isLeadership = !!user?.leadership_level;
  const links = [
    { name: 'Home', path: '/portal', icon: Home, module: 'portal_community' },
    { name: 'My Profile', path: `/portal/members/${userId}`, icon: User, module: null },
    { name: 'Members', path: '/portal/member-directory', icon: Users, module: null },
    { name: 'Business', path: '/portal/business-directory', icon: Building2, module: null },
    { name: 'Member Deals', path: '/portal/member-deals', icon: Gift, module: null },
    { name: 'Dashboard', path: '/portal/dashboard', icon: LayoutDashboard, module: 'portal_home' },
    { name: 'Events', path: '/portal/events', icon: Calendar, module: 'portal_events' },
    { name: 'Committees', path: '/portal/committees', icon: Users, module: 'portal_committees' },
    { name: 'Requests', path: '/portal/requests', icon: Send, module: 'portal_requests' },
    { name: 'Blog Submission', path: '/portal/blog', icon: FileText, module: 'portal_blog' },
    { name: 'Social Media Request', path: '/portal/social', icon: Share2, module: 'portal_social' },
    ...(!isLeadership ? [{ name: 'Company Profile', path: '/portal/profile', icon: Building2, module: 'portal_profile' }] : []),
    ...(isLeadership ? [{ name: 'My Leadership Profile', path: '/portal/my-leadership', icon: Award, module: null }] : []),
    { name: 'Messages', path: '/portal/messages', icon: Mail, module: null },
    { name: 'Membership Support', path: '/portal/support', icon: Headphones, module: 'portal_support' },
  ];
  return links;
};

const MemberPortalLayout = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const portalLinks = getPortalLinks(user?.id, user);
  const isLeadership = !!user?.leadership_level;

  // Fetch unread messages count
  useEffect(() => {
    const fetchUnread = async () => {
      try {
        const res = await axios.get(`${API}/portal/messages/unread-count`, { withCredentials: true });
        setUnreadCount(res.data.count || 0);
      } catch { /* */ }
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    return () => clearInterval(interval);
  }, []);

  // Filter portal links based on module_permissions
  const filteredPortalLinks = portalLinks.filter(link => {
    if (!link.module) return true; // No module restriction (My Profile, Messages)
    const perms = user?.module_permissions;
    if (!perms || typeof perms !== 'object') return true;
    if (link.module in perms) return perms[link.module] === true;
    return true;
  });

  const handleLogout = async () => {
    await logout();
    navigate('/member-login');
  };

  const currentPage = filteredPortalLinks.find(
    l => l.path === location.pathname || (l.path !== '/portal' && location.pathname.startsWith(l.path))
  );

  return (
    <div className="min-h-screen bg-[#F5F6FA]">
      <ForcePasswordChange />
      {/* ===== TOP NAVBAR — Warm, social, horizontal ===== */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-200/80 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            {/* Left: Logo + brand */}
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setMobileMenuOpen(true)}
                className="lg:hidden p-2 -ml-2 text-slate-600 hover:text-[#E63946]"
                data-testid="portal-mobile-menu"
              >
                <Menu className="w-5 h-5" />
              </button>
              <Link to="/portal" className="flex items-center space-x-2.5" data-testid="portal-logo">
                <Img variant="generic"
                  src="/brand/logo-color.png"
                  alt="DCCF" className="h-9"
                />
                <div className="hidden sm:block">
                  <p className="text-sm font-bold text-[#002249] leading-tight">DCCF</p>
                  <p className="text-[10px] text-slate-400 leading-tight">Member Portal</p>
                </div>
              </Link>
            </div>

            {/* Center: Desktop nav (condensed top links for most-used) */}
            <nav className="hidden lg:flex items-center space-x-1" data-testid="portal-top-nav">
              {filteredPortalLinks.slice(0, 5).map(link => {
                const isActive = link.path === '/portal'
                  ? location.pathname === '/portal'
                  : link.path.includes('/portal/members/')
                    ? location.pathname.startsWith('/portal/members/')
                    : location.pathname.startsWith(link.path);
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`flex items-center space-x-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                      isActive
                        ? 'bg-[#E63946]/10 text-[#E63946]'
                        : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                    }`}
                    data-testid={`portal-nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <link.icon className="w-4 h-4" />
                    <span>{link.name}</span>
                  </Link>
                );
              })}
            </nav>

            {/* Right: Messages + Notifications + User avatar */}
            <div className="flex items-center gap-0.5 sm:gap-2">
              <Link to="/portal/messages" className="relative p-1.5 sm:p-2 text-slate-500 hover:text-[#E63946] hover:bg-slate-50 rounded-lg transition-all" data-testid="portal-messages-icon">
                <MessageCircle className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-[#E63946] text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 shadow-sm" data-testid="unread-messages-count">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </Link>
              <NotificationBell />
              <Link to="/" className="hidden sm:flex p-2 text-slate-500 hover:text-[#002249] hover:bg-slate-50 rounded-lg transition-all" data-testid="portal-back-to-site" title="Visit Website">
                <Globe className="w-5 h-5" />
              </Link>
              {(user?.admin_access || ['super_admin', 'membership_lead', 'marketing_team'].includes(user?.role)) && (
                <Link to="/admin" className="flex items-center gap-1 px-2 py-1.5 text-[#002249] bg-[#002249]/8 hover:bg-[#002249]/15 rounded-lg transition-all text-[11px] sm:text-xs font-semibold" data-testid="portal-admin-link" title="Admin Portal">
                  <Settings className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">Admin</span>
                </Link>
              )}
              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center space-x-2 pl-2 pr-3 py-1.5 rounded-full hover:bg-slate-50 transition-all"
                  data-testid="portal-user-menu"
                >
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#E63946] to-[#F4A261] flex items-center justify-center text-white font-bold text-xs overflow-hidden flex-shrink-0" data-testid="nav-profile-avatar">
                    {user?.picture ? (
                      <Img variant="avatar" src={resolveImg(user.picture)} alt="" className="w-full h-full object-cover" data-testid="nav-profile-photo" />
                    ) : user?.name?.charAt(0).toUpperCase() || 'M'}
                  </div>
                  <span className="hidden sm:block text-sm font-medium text-slate-700 max-w-[100px] truncate">{(user?.display_name || user?.name || 'Member').split(' ')[0]}</span>
                  <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                </button>
                <AnimatePresence>
                  {userMenuOpen && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setUserMenuOpen(false)} />
                      <motion.div
                        initial={{ opacity: 0, y: -4, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -4, scale: 0.98 }}
                        transition={{ duration: 0.18, ease: [0.2, 0, 0, 1] }}
                        className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-slate-200 z-50 overflow-hidden"
                        data-testid="user-dropdown"
                      >
                        <div className="px-4 py-3 border-b border-slate-100">
                          <p className="text-sm font-semibold text-slate-900">{user?.display_name || user?.name}</p>
                          <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                          <span className="inline-block mt-1.5 bg-green-100 text-green-700 text-[10px] font-semibold px-2 py-0.5 rounded-full">Active Member</span>
                        </div>
                        <div className="py-1">
                          <Link to={`/portal/members/${user?.id}`} onClick={() => setUserMenuOpen(false)} className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-50" data-testid="view-my-profile">View Profile</Link>
                          {!isLeadership && <Link to="/portal/social-profile" onClick={() => setUserMenuOpen(false)} className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-50" data-testid="edit-social-profile">Edit Social Profile</Link>}
                          {!isLeadership && <Link to="/portal/profile" onClick={() => setUserMenuOpen(false)} className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-50">Edit Company Profile</Link>}
                          {isLeadership && <Link to="/portal/my-leadership" onClick={() => setUserMenuOpen(false)} className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-50" data-testid="edit-leadership-profile">My Leadership Profile</Link>}
                          <Link to="/portal/support" onClick={() => setUserMenuOpen(false)} className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-50">Support</Link>
                          {(user?.admin_access || ['super_admin', 'membership_lead', 'marketing_team'].includes(user?.role)) && (
                            <Link to="/admin" onClick={() => setUserMenuOpen(false)} className="block px-4 py-2 text-sm text-[#002249] font-semibold hover:bg-slate-50" data-testid="dropdown-admin-link">
                              Admin Portal
                            </Link>
                          )}
                          <Link to="/" onClick={() => setUserMenuOpen(false)} className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-50">Visit Website</Link>
                        </div>
                        <div className="border-t border-slate-100 py-1">
                          <button onClick={handleLogout} className="block w-full text-left px-4 py-2 text-sm text-[#E63946] hover:bg-red-50" data-testid="portal-logout">
                            Sign Out
                          </button>
                        </div>
                      </motion.div>
                    </>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>

        {/* Desktop secondary nav for remaining links */}
        <div className="hidden lg:block border-t border-slate-100 bg-slate-50/60">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex items-center space-x-1 h-10 overflow-x-auto">
              {filteredPortalLinks.slice(5).map(link => {
                const isActive = link.path === '/portal'
                  ? location.pathname === '/portal'
                  : link.path.includes('/portal/members/')
                    ? location.pathname.startsWith('/portal/members/')
                    : location.pathname.startsWith(link.path);
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition-all ${
                      isActive
                        ? 'bg-[#002249]/10 text-[#002249]'
                        : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
                    }`}
                    data-testid={`portal-nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <link.icon className="w-3.5 h-3.5" />
                    <span>{link.name}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </header>

      {/* ===== MOBILE SLIDE-OUT MENU ===== */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }} className="fixed inset-0 u-glass z-50 lg:hidden" onClick={() => setMobileMenuOpen(false)} />
            <motion.aside
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'tween', duration: 0.25 }}
              className="fixed top-0 left-0 bottom-0 w-72 bg-white z-50 lg:hidden shadow-xl flex flex-col"
            >
              <div className="flex items-center justify-between px-4 h-16 border-b border-slate-100">
                <Link to="/portal" onClick={() => setMobileMenuOpen(false)} className="flex items-center space-x-2">
                  <Img variant="generic" src="/brand/logo-color.png" alt="DCCF" className="h-8" />
                  <span className="font-bold text-[#002249] text-sm">DCCF Member</span>
                </Link>
                <button onClick={() => setMobileMenuOpen(false)} className="p-2 text-slate-400 hover:text-slate-600">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* User card */}
              <div className="p-4 border-b border-slate-100">
                <Link to={`/portal/members/${user?.id}`} onClick={() => setMobileMenuOpen(false)} className="flex items-center space-x-3">
                  <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#E63946] to-[#F4A261] flex items-center justify-center text-white font-bold text-sm overflow-hidden">
                    {user?.picture ? <Img variant="avatar" src={resolveImg(user.picture)} alt="" className="w-full h-full object-cover" /> : user?.name?.charAt(0).toUpperCase() || 'M'}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{user?.display_name || user?.name}</p>
                    <p className="text-xs text-slate-500">{user?.email}</p>
                  </div>
                </Link>
              </div>

              {/* Nav links */}
              <nav className="flex-1 overflow-y-auto p-3 space-y-0.5">
                {filteredPortalLinks.map(link => {
                  const isActive = link.path === '/portal'
                    ? location.pathname === '/portal'
                    : link.path.includes('/portal/members/')
                      ? location.pathname.startsWith('/portal/members/')
                      : location.pathname.startsWith(link.path);
                  return (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`flex items-center space-x-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                        isActive
                          ? 'bg-[#E63946]/10 text-[#E63946]'
                          : 'text-slate-700 hover:bg-slate-50'
                      }`}
                      data-testid={`portal-mobile-nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <link.icon className="w-5 h-5 flex-shrink-0" />
                      <span>{link.name}</span>
                    </Link>
                  );
                })}
              </nav>

              <div className="p-3 border-t border-slate-100 space-y-1">
                <Link to="/" className="flex items-center space-x-3 px-3 py-2.5 text-slate-600 hover:bg-slate-50 rounded-lg text-sm">
                  <Globe className="w-5 h-5" /><span>Visit Website</span>
                </Link>
                <button onClick={handleLogout} className="w-full flex items-center space-x-3 px-3 py-2.5 text-[#E63946] hover:bg-red-50 rounded-lg text-sm font-medium">
                  <LogOut className="w-5 h-5" /><span>Sign Out</span>
                </button>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* ===== MAIN CONTENT ===== */}
      <main className="pt-16 lg:pt-[104px] min-h-screen">
        <div className="max-w-7xl mx-auto">
          <PageTransition>
            <Outlet />
          </PageTransition>
        </div>
      </main>
    </div>
  );
};

export default MemberPortalLayout;
